import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container } from '@mui/material';
import TestHistoryList from './TestHistoryList';
import TestResultsFilters from './TestResultsFilters';
import {
  selectSearchResults,
  selectSearchLoading,
  selectSearchError,
  selectLastSearchFilters,
} from '../../redux/testHistory/testHistorySlice';

/**
 * TestHistoryPage - Main container for test history list view
 * Search form is displayed in the left panel
 */
export default function TestHistoryPage() {
  const navigate = useNavigate();

  const results = useSelector(selectSearchResults);
  const loading = useSelector(selectSearchLoading);
  const error = useSelector(selectSearchError);
  const lastSearchFilters = useSelector(selectLastSearchFilters);

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [filters, setFilters] = useState({
    flowName: searchParams.get('flowName') || '',
    testCaseName: searchParams.get('testCaseName') || '',
    deviceName: searchParams.get('deviceName') || '',
    userEmail: searchParams.get('userEmail') || '',
    market: searchParams.get('market') || '',
    sortBy: searchParams.get('sortBy') || 'newest',
  });

  useEffect(() => {
    const params = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value) params[key] = value;
    });

    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      flowName: '',
      testCaseName: '',
      deviceName: '',
      userEmail: '',
      market: '',
      sortBy: 'newest',
    });
  };

  // Apply filters and sorting
  const filteredAndSortedResults = useMemo(() => {
    let filtered = [...results];

    // Apply filters
    if (filters.flowName) {
      filtered = filtered.filter((r) => r.flow_name === filters.flowName);
    }
    if (filters.testCaseName) {
      filtered = filtered.filter((r) => r.test_case_name === filters.testCaseName);
    }
    if (filters.deviceName) {
      filtered = filtered.filter((r) => r.device_name === filters.deviceName);
    }
    if (filters.userEmail) {
      filtered = filtered.filter((r) => r.user_email === filters.userEmail);
    }
    if (filters.market) {
      filtered = filtered.filter((r) => r.market === filters.market);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (filters.sortBy === 'newest') {
        return b.timestamp_start - a.timestamp_start;
      } else {
        return a.timestamp_start - b.timestamp_start;
      }
    });

    return filtered;
  }, [results, filters]);

  // Check if any filters are active
  const hasActiveFilters = !!(
    filters.flowName ||
    filters.testCaseName ||
    filters.deviceName ||
    filters.userEmail ||
    filters.market
  );

  const handleCardClick = (result) => {
    console.log('Navigating to test result detail:', result.id);
    navigate(`/test-history/${result.id}`, {
      state: {
        from: location.pathname + location.search, // FULL filtered URL
      },
    });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Show filters only when we have results */}
      {!loading && results.length > 0 && (
        <TestResultsFilters
          results={results}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />
      )}

      <TestHistoryList
        results={filteredAndSortedResults}
        allResults={results}
        totalResults={results.length}
        loading={loading}
        error={error}
        onCardClick={handleCardClick}
        sortBy={filters.sortBy}
        onSortChange={handleFilterChange}
        hasActiveFilters={hasActiveFilters}
        searchFilters={lastSearchFilters}
      />
    </Container>
  );
}
